// HubSpot iframe context - COMPATIBILITY SHIM.
//
// The verified postMessage protocol now lives in ./hubspot-handshake.ts and
// its React layer in ./session-context.tsx. This file keeps the pre-R1
// public surface alive so existing imports in App.tsx, tabs, and screens do
// not need to change in the same commit as the auth rewrite. New code
// should import from ./session-context.js (SessionProvider, useSession).
//
// Key behavior change from the pre-R1 implementation:
//   - The listener now verifies event.source === window.parent in addition
//     to event.origin. Without the source check, any nested iframe inside
//     the HubSpot page could spoof the context (HIGH-severity hole flagged
//     during R0 scoping, closed here).
//   - The listener stays attached for the lifetime of the iframe so the
//     context updates when the rep navigates between HubSpot records.
//   - Tokens carry an expiresAt. A refresh is requested at 80% of TTL. If
//     the parent does not deliver a fresh session before expiresAt, the
//     context flips to 'error' with a user-visible banner message.

import {
  createElement,
  useMemo,
  type ReactNode,
} from 'react';

import {
  SessionProvider,
  useSession,
} from './session-context.js';
import {
  parseSessionPayload,
  __test__ as handshakeTestExports,
  MSG,
  type HubSpotSession,
} from './hubspot-handshake.js';

// --- Legacy types (preserved) --------------------------------------------

export interface HubSpotContext {
  userId: string;
  userEmail: string;
  portalId: number;
  objectId: string | null;
  contextToken: string;
  receivedAt: number;
}

export type HubSpotContextState =
  | { status: 'pending' }
  | { status: 'ready'; context: HubSpotContext }
  | { status: 'error'; error: HubSpotContextError };

export interface HubSpotContextError {
  code:
    | 'HANDSHAKE_TIMEOUT'
    | 'HANDSHAKE_INVALID'
    | 'HANDSHAKE_UNTRUSTED_ORIGIN'
    | 'HANDSHAKE_UNTRUSTED_SOURCE'
    | 'TOKEN_EXPIRED'
    | 'REFRESH_FAILED'
    | 'MOCK_UNAVAILABLE';
  message: string;
}

export interface HubSpotContextProviderProps {
  children: ReactNode;
  windowRef?: Window;
  forceMock?: boolean;
  // Deprecated: the new handshake does not time out on no reply; the
  // 'pending' status is sticky until the parent delivers a session. Kept
  // in the type for source compatibility; ignored at runtime.
  timeoutMs?: number;
}

// --- Legacy provider -----------------------------------------------------

export function HubSpotContextProvider(
  props: HubSpotContextProviderProps,
): ReactNode {
  const { children, windowRef, forceMock } = props;
  return createElement(
    SessionProvider,
    {
      children,
      ...(windowRef !== undefined ? { windowRef } : {}),
      ...(forceMock !== undefined ? { forceMock } : {}),
    },
    children,
  );
}

function toLegacyContext(session: HubSpotSession): HubSpotContext {
  return {
    userId: session.userId,
    userEmail: session.email,
    portalId: session.portalId,
    objectId: session.context.objectId || null,
    contextToken: session.token,
    receivedAt: session.receivedAt,
  };
}

export function useHubSpotContext(): HubSpotContextState {
  const s = useSession();
  return useMemo<HubSpotContextState>(() => {
    // 'refreshing' and 'ready' both expose the previous session so the UI
    // keeps rendering context. 'expired' is a user-visible failure.
    if ((s.status === 'ready' || s.status === 'refreshing') && s.session) {
      return { status: 'ready', context: toLegacyContext(s.session) };
    }
    if (s.status === 'pending') return { status: 'pending' };
    if (s.status === 'expired' || s.status === 'error') {
      const err = s.error ?? {
        code: 'HANDSHAKE_INVALID' as const,
        message: 'HubSpot session is unavailable.',
      };
      // Narrow error.code to the legacy union; unknown codes fall back to
      // HANDSHAKE_INVALID so legacy consumers get a stable shape.
      const allowed: readonly string[] = [
        'HANDSHAKE_INVALID',
        'HANDSHAKE_UNTRUSTED_ORIGIN',
        'HANDSHAKE_UNTRUSTED_SOURCE',
        'TOKEN_EXPIRED',
        'REFRESH_FAILED',
        'MOCK_UNAVAILABLE',
      ];
      const legacyCode: HubSpotContextError['code'] = allowed.includes(err.code)
        ? (err.code as HubSpotContextError['code'])
        : 'HANDSHAKE_INVALID';
      return {
        status: 'error',
        error: { code: legacyCode, message: err.message },
      };
    }
    return { status: 'pending' };
  }, [s]);
}

export function useHubSpotContextOrThrow(): HubSpotContext {
  const state = useHubSpotContext();
  if (state.status !== 'ready') {
    throw new Error(
      `HubSpot context is not ready (status=${state.status}). ` +
        'Wrap the caller with a guard that renders a fallback while status is pending or error.',
    );
  }
  return state.context;
}

// --- Legacy test-only exports --------------------------------------------
//
// The pre-R1 tests used these to drive internals. The named members below
// map to equivalents in the new handshake module. The 'extractContext' name
// is kept for source compatibility; the new parser is stricter (fields are
// top-level, not inside a 'payload' envelope) and the shape it returns
// matches HubSpotSession, so legacy callers see parseSessionPayload here.
export const __test__ = {
  isTrustedOrigin: handshakeTestExports.isTrustedOrigin,
  extractContext: (payload: unknown, receivedAt: number) =>
    parseSessionPayload(payload, receivedAt),
  EXPECTED_MESSAGE_TYPE: MSG.SESSION,
  READY_MESSAGE_TYPE: MSG.HELLO,
};
